/*
 *  (c) 2015 - 2024 ENISCO by FORCAM GmbH
 */

import {FavoriteApp, Group} from '../../../../model/favorites'
import React, {useCallback, useState} from 'react'
import {useDrop} from 'react-dnd'
import {clone, TranslatedText, translateText, useLanguage, useThemeName} from '@enisco/mcc-react-lib'
import useRegisteredApps from '../../../../hooks/useRegisteredApps'
import FavoriteViewerFavorite from './FavoriteViewerFavorite'
import {Collapse, List, ListItem, ListItemText} from '@mui/material'
import {ExpandLess, ExpandMore} from '@mui/icons-material'
import {useTheme} from '@mui/material/styles'

interface Props {
  handleClose: () => void
  group: Group
  moveFavorite: (favorite: FavoriteApp, group: Group) => void
}

interface FavoriteItem {
  index: number
  favoriteApp: FavoriteApp
}

const FavoriteViewerGroup: React.FC<Props> = ({
  handleClose,
  group,
  moveFavorite
}) => {
  const language = useLanguage()
  const theme = useTheme()
  const [themeName] = useThemeName()
  const [registeredApps] = useRegisteredApps()
  const [open, setOpen] = useState(false)

  const handleClick = () => {
    setOpen(!open)
  }

  const sortedFavorites = clone(group.favorites ?? [])
  sortedFavorites.sort((a, b) =>
    (translateText(language, a.description) ?? '').localeCompare(
      translateText(language, b.description) ?? ''
    )
  )

  const favoriteApps: FavoriteApp[] = []
  sortedFavorites.forEach((favorite) => {
    const app = registeredApps.find(
      (regApp) =>
        regApp.app === favorite.app.app && regApp.module === favorite.app.module
    )
    if (app) {
      favoriteApps.push({
        app: app,
        favorite: favorite
      })
    }
  })

  const [{canDrop, isOver, isDragging}, drop] = useDrop(() => ({
    accept: 'Favorite',
    canDrop: (item: FavoriteItem) => {
      if (item.favoriteApp.favorite.groupId === group.id) {
        return false
      }

      if (item.favoriteApp.favorite.user === undefined) {
        return group.user === undefined
      }

      return true
    },
    drop(item: FavoriteItem) {
      moveFavorite(item.favoriteApp, group)
    },
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
      isDragging: monitor.getItemType() !== null
    })
  }))

  const getFavoriteApp = useCallback(
    (favoriteApp: FavoriteApp, index: number) => {
      return (
        <FavoriteViewerFavorite
          favoriteApp={favoriteApp}
          key={index}
          index={index}
          handleClose={handleClose}
        />
      )
    },
    [handleClose]
  )

  return (
    <div ref={(r) => {
      drop(r)
    }}>
      <ListItem
        onClick={() => handleClick()}
        data-testid={'open-list-' + group.id}
        sx={{
          opacity: (isOver || isDragging) && !canDrop ? 0.33 : 1,
          backgroundColor: theme.palette.background.paper,
          '&:hover': {
            backgroundColor: themeName === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        <ListItemText primary={<TranslatedText text={group.description} />} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={open} timeout='auto' unmountOnExit>
        <List
          component='div'
          disablePadding
          data-testid={'group-list-' + group.id}
        >
          <div style={{
            alignContent: 'center',
            opacity: (isOver || isDragging) && !canDrop ? 0.33 : 1,
            backgroundColor: theme.palette.background.paper
          }}>
            {favoriteApps.map(getFavoriteApp)}
          </div>
        </List>
      </Collapse>
    </div>
  )
}

export default FavoriteViewerGroup
